import { Component, inject, input, signal } from '@angular/core'
import { OfferFormService } from '../shared/services/offer-form.service'
import { MatStepper } from '@angular/material/stepper'
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms'
import { RequiredFieldsComponent } from '../shared/components/required-fields/required-fields.component'
import {
  BricksAlertComponent,
  BricksAutocompleteComponent,
  BricksCardComponent,
  BricksDropdownComponent,
  BricksErrorComponent,
  BricksIconComponent,
  BricksInputComponent,
  BricksOptionComponent,
} from '@enbw_bricks/ui'
import { RequiredAsteriskComponent } from '../shared/components/required-asterisk/required-asterisk.component'
import { useAddressLogic } from './composables/use-address-logic'
import { useContractPartnerLogic } from './composables/use-contract-partner-logic'
import { useInputRestrictions } from './composables/use-input-restrictions'
import { NotificationService } from '../shared/components/notification/notification.service'
import { ContactFormControls } from '../shared/models/main-form'
import { AbstractTrackEventsService } from '../shared/services/abstract-track-events.service'
import { MarketingType } from '../shared/models/marketing-type.enum'
import { CommercialRegisterService, CompanyDataValidationResult } from '../shared/services/commercial-register.service'
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http'
import { registerTypes, registrationAuthorityNames } from '../shared/models/commercial-register-data'
import { normalizeRegistrationNumber } from '../shared/utils/registration-number'
import { IndicativeOfferService } from '../shared/services/indicative-offer.service'
import { MatDialog } from '@angular/material/dialog'
import { ErrorMessageComponent } from '../overview/error-message/error-message.component'
import { CustomValidators } from '../shared/validators/custom-validators'

@Component({
  selector: 'app-contact',
  imports: [
    ReactiveFormsModule,
    RequiredFieldsComponent,
    BricksCardComponent,
    RequiredAsteriskComponent,
    BricksAlertComponent,
    BricksIconComponent,
    BricksInputComponent,
    BricksErrorComponent,
    BricksDropdownComponent,
    BricksOptionComponent,
    BricksAutocompleteComponent,
  ],
  templateUrl: './contact.component.html',
  styleUrls: ['./contact.component.scss'],
})
export class ContactComponent {
  private readonly trackEventService = inject(AbstractTrackEventsService)
  private readonly offerFormService = inject(OfferFormService)
  private readonly indicativeOfferService = inject(IndicativeOfferService)
  private readonly dialog = inject(MatDialog)
  stepper = input<MatStepper>()
  formGroup: FormGroup<ContactFormControls>
  readonly addressLogic: ReturnType<typeof useAddressLogic>
  readonly contractLogic: ReturnType<typeof useContractPartnerLogic>
  readonly inputRestrictions: ReturnType<typeof useInputRestrictions>
  readonly isCommercialRegisterDataApplied = signal(false)
  readonly isContractOwnerNameDisabled = signal(false)
  readonly isAddressPostalCodeDisabled = signal(false)
  readonly isAddressCityDisabled = signal(false)
  readonly isAddressStreetDisabled = signal(false)
  readonly isAddressHouseNumberDisabled = signal(false)

  private readonly notificationService = inject(NotificationService)
  private readonly commercialRegisterService = inject(CommercialRegisterService)

  constructor() {
    this.formGroup = this.offerFormService.contactData

    this.addressLogic = useAddressLogic(this.formGroup)
    this.contractLogic = useContractPartnerLogic(this.formGroup)
    this.inputRestrictions = useInputRestrictions()
  }

  getAddressPostalCodeError(control: FormControl): string {
    if (control.hasError('noCitiesFound')) {
      return 'Diese PLZ existiert nicht. Bitte überprüfen Sie die Eingabe.'
    } else if (control.hasError('minlength')) {
      return 'Die Eingabe muss 5 Zeichen lang sein.'
    }
    return ''
  }

  getAddressStreetError(control: FormControl): string {
    if (control.hasError('invalidStreet')) {
      return 'Diese Straße existiert nicht. Bitte wählen Sie eine gültige Straße aus.'
    }
    return ''
  }

  onSubmit() {
    if (this.formGroup.valid) {
      this.indicativeOfferService
        .sendIndicativeOfferRequest(
          this.offerFormService.generalData,
          this.offerFormService.contactData,
          this.offerFormService.priceData
        )
        .subscribe({
          error: () => {
            this.showErrorModal()
          },
        })
      this.notificationService.clear()
    } else {
      this.formGroup.markAllAsTouched()
      this.notificationService.missingDataError()
    }
  }

  showErrorModal() {
    this.dialog.open(ErrorMessageComponent, {
      width: '640px',
      maxWidth: 'none',
      maxHeight: 'none',
    })
  }

  trackEvent() {
    this.trackEventService.trackEventRequestSubmitted({
      event: 'offer_request_submitted',
      eventCategory: 'Form',
      eventAction: 'submit',
      eventLabel: 'Request Individual Offer',
      technology_type: this.offerFormService.generalData.controls.productionTechnology.value,
      direct_marketing_type: this.marketingTypeToEventLabel(),
      partner_type: this.offerFormService.contactData.controls.contractPartner.value,
    })
  }

  private marketingTypeToEventLabel(): string {
    return this.offerFormService.generalData.controls.marketingType.value === MarketingType.EEGDirectMarketing
      ? 'eeg'
      : 'other'
  }

  isFetchCommercialRegisterDataButtonDisabled(): boolean {
    return !(
      this.hasValue(this.formGroup.controls.registrationNumber.value) &&
      this.hasValue(this.formGroup.controls.registrationType.value) &&
      this.hasValue(this.formGroup.controls.registrationAuthorityName.value)
    )
  }

  private hasValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== ''
  }

  fetchCommercialRegisterData() {
    this.trackEventService.trackEvent('commercial_register_fetch', 'Button', 'click', 'Fetch Company Data')
    this.clearCompanyAddressFields()
    this.clearCommercialRegisterFieldLocks()
    const { registrationType, registrationAuthorityName, registrationNumber } = this.formGroup.controls
    const compactNumber = normalizeRegistrationNumber(registrationNumber.value) ?? ''
    if (compactNumber !== '') {
      registrationNumber.setValue(compactNumber, { emitEvent: false })
    }
    this.commercialRegisterService
      .getCompanyData({
        registrationType: String(registrationType.value ?? ''),
        registrationAuthorityName: String(registrationAuthorityName.value ?? ''),
        registrationNumber: compactNumber,
      })
      .subscribe({
        next: (results) => this.handleCompanyDataSuccess(results),
        error: (error: unknown) => this.handleCompanyDataError(error),
      })
  }

  resetCommercialRegisterData(): void {
    this.formGroup.controls.registrationType.reset()
    this.formGroup.controls.registrationNumber.reset()
    this.formGroup.controls.registrationAuthorityName.reset()
    this.addressLogic.resetFetchedData()
    this.clearCommercialRegisterFieldLocks()
  }

  private handleCompanyDataSuccess(results: CompanyDataValidationResult[]): void {
    const companyDataResult = results.length === 1 ? results[0] : undefined
    if (!companyDataResult) {
      this.showCompanyNotFound()
      this.trackEventService.trackEvent('commercial_register_fetch_error', 'Button', 'error', 'Fetch Company Data')
      return
    }
    const companyName = String(companyDataResult.name ?? '').trim()
    const postalCode = String(companyDataResult.address.postalCode ?? '').trim()
    const city = String(companyDataResult.address.city ?? '').trim()
    const street = String(companyDataResult.address.street ?? '').trim()
    const houseNumber = String(companyDataResult.address.houseNumber ?? '').trim()

    if (companyName) {
      this.formGroup.controls.contractOwnerName.setValue(companyName, { emitEvent: false })
    }

    if (houseNumber) {
      this.formGroup.controls.addressHouseNumber.setValue(houseNumber, { emitEvent: false })
    }

    this.addressLogic.setHouseNumberLock(!!houseNumber)
    this.addressLogic.applyFetchedAddress({ postalCode, city, street })

    this.isContractOwnerNameDisabled.set(!!companyName)
    this.isAddressPostalCodeDisabled.set(!!postalCode)
    this.isAddressCityDisabled.set(!!city)
    this.isAddressStreetDisabled.set(!!street)
    this.isAddressHouseNumberDisabled.set(!!houseNumber)
    this.isCommercialRegisterDataApplied.set(!!(companyName || postalCode || city || street || houseNumber))

    this.notificationService.confirm('Datenabruf erfolgreich.', '')
    this.trackEventService.trackEvent('commercial_register_fetch_success', 'Button', 'success', 'Fetch Company Data')
  }

  private handleCompanyDataError(error: unknown): void {
    this.trackEventService.trackEvent('commercial_register_fetch_error', 'Button', 'error', 'Fetch Company Data')

    const statusCode = error instanceof HttpErrorResponse ? error.status : undefined

    if (statusCode === HttpStatusCode.NotFound || statusCode === HttpStatusCode.BadRequest) {
      this.showCompanyNotFound()
      return
    }
    this.notificationService.error(
      'Verbindung zum Handelsregister fehlgeschlagen',
      'Ein technischer Fehler ist aufgetreten. Der Abruf der Handelsregisterdaten ist momentan nicht möglich. Bitte versuchen Sie es später erneut oder kontaktieren Sie unseren Support.'
    )
  }

  private clearCompanyAddressFields(): void {
    this.formGroup.controls.contractOwnerName.reset()
    this.formGroup.controls.addressCity.reset()
    this.formGroup.controls.addressPostalCode.reset()
    this.formGroup.controls.addressStreet.reset()
    this.formGroup.controls.addressHouseNumber.reset()
    this.addressLogic.clearPrefilledAddress()
  }

  private clearCommercialRegisterFieldLocks(): void {
    this.addressLogic.clearFieldLocks()
    this.isCommercialRegisterDataApplied.set(false)
    this.isContractOwnerNameDisabled.set(false)
    this.isAddressPostalCodeDisabled.set(false)
    this.isAddressCityDisabled.set(false)
    this.isAddressStreetDisabled.set(false)
    this.isAddressHouseNumberDisabled.set(false)
  }

  onRegistrationNumberBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement | null
    const normalized =
      normalizeRegistrationNumber(input?.value ?? this.formGroup.controls.registrationNumber.value) ?? ''
    if (input && input.value !== normalized) input.value = normalized
    this.formGroup.controls.registrationNumber.setValue(normalized === '' ? null : normalized)
  }

  private showCompanyNotFound(): void {
    this.notificationService.error(
      'Unternehmen nicht gefunden',
      'Bitte überprüfen Sie den Registertyp, die Registernummer und die Zuständigkeit des Registergerichts. Es können nur Unternehmen mit dem Status „Aktuell“ abgerufen werden.'
    )
  }

  onBack() {
    this.stepper()?.previous()
  }

  protected registrationAuthorityNames = [...registrationAuthorityNames]
  protected registerTypes = [...registerTypes]
  protected readonly registrationNumberFormatError = CustomValidators.registrationNumberFormatErrorMessage
}
